import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import DropdownSelect, { DropdownItem } from 'app/components/DropdownSelect';
import NavigationDefault from 'app/components/Navigation/NavigationDefault';
import { NavigationDetail } from 'app/components/Navigation/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';

import DeviceConnection from 'app/modules/alerts/components/DeviceConnection';
import { DROPDOWN_BUTTON_TEXT, DROPDOWN_SELECT_QUERY_KEY } from 'app/modules/alerts/core/constant';
import {
  getAlertSuppression,
  getTotalUnreadAlert,
  updateAlertSuppression,
  updateMarkAllAlert,
} from 'app/modules/alerts/core/request';
import { Alert, AlertType, SuppressionResponse, TAB_ALERTS } from 'app/modules/alerts/core/type';
import { queryToTabMap, tabToQueryMap } from 'app/modules/alerts/core/util';
import { useAlertStore } from 'app/modules/alerts/store';
import { Header } from 'app/modules/notifications/NotificationPage';

import { isNumber } from 'lodash';

import queryString from 'query-string';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ItemExpiry = lazy(() => import('app/modules/alerts/components/ItemExpiry'));
const ReachCriticalLevel = lazy(() => import('app/modules/alerts/components/ReachCriticalLevel'));

const AlertsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = queryString.parse(location.search);
  const tabQuery = query.tab as string;

  const [tab, setTab] = useState<TAB_ALERTS>(() => {
    const mappedTab = queryToTabMap[tabQuery];
    return isNumber(mappedTab) ? mappedTab : TAB_ALERTS.DEVICE_CONNECTION;
  });

  const alertType = useMemo(
    () =>
      ({
        [TAB_ALERTS.DEVICE_CONNECTION]: AlertType['Rack Failed Connection'],
        [TAB_ALERTS.ITEM_EXPIRY]: AlertType['Shelf Expiry'],
        [TAB_ALERTS.REACH_CRITICAL_LEVEL]: AlertType['Reach Critical Level'],
        [TAB_ALERTS.ABNORMAL_CONSUMPTION]: AlertType['Abnormal Consumption'],
      }[tab]),
    [tab],
  );

  const unReadCount = useAlertStore((s) => s.state.unReadCount);
  const fetched = useAlertStore((s) => s.state.fetched);
  const organization = useOrganizationStore((state) => state.state.organization);
  const alertGroup = useAlertStore((s) => s.state.alertGroup);

  const { data: alertSuppressionItems = [] } = useQuery<SuppressionResponse[]>({
    queryKey: ['alertSuppression', alertType, organization?.id],
    queryFn: () => getAlertSuppression(alertType),
    staleTime: 30000,
    enabled: !!alertType && !!organization?.id,
  });

  useEffect(() => {
    if (alertSuppressionItems.length > 0) {
      useAlertStore.getState().setState({
        alertSuppressionItems,
      });
    }
  }, [alertSuppressionItems]);

  const dropdownItems = useMemo(
    () =>
      alertSuppressionItems?.map((item) => ({
        id: item.id,
        title: item.name,
        subtitle:
          tab === TAB_ALERTS.DEVICE_CONNECTION
            ? `${item.active ? 'Suppressed' : 'Active'}`
            : `SKU: ${item.sku} - ${item.active ? 'Suppressed' : 'Active'}`,
        sku: item.sku,
        selected: item.active,
      })),
    [alertSuppressionItems, tab],
  );

  const { mutateAsync: updateAlertSuppressionMutation } = useMutation<
    void,
    Error,
    { relation: string | number; active: boolean }[]
  >({
    mutationKey: ['update-alert-suppression', alertType],
    mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
      updateAlertSuppression(alertType, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['alertSuppression', alertType, organization?.id]);
    },
  });

  useEffect(() => {
    const mappedTab = queryToTabMap[tabQuery];

    if (isNumber(mappedTab)) {
      setTab(mappedTab);
      return;
    }

    navigate(`?tab=${tabToQueryMap[TAB_ALERTS.DEVICE_CONNECTION]}`);
  }, [tabQuery]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(['alerts']);
      queryClient.setQueryData(['alerts'], undefined);
      useAlertStore.getState().setState({
        fetched: false,
        total: 0,
      });
    };
  }, []);

  const navigation: NavigationDetail[] = [
    {
      title: `Device connection (${alertGroup?.['Rack Failed Connection']?.total || 0})`,
      value: TAB_ALERTS.DEVICE_CONNECTION,
      content: <DeviceConnection tab={tab} />,
    },
    {
      title: `Item expiry (${alertGroup?.['Shelf Expiry']?.total || 0})`,
      value: TAB_ALERTS.ITEM_EXPIRY,
      content: (
        <Suspense fallback={<LoadingCard />}>
          <ItemExpiry tab={tab} />
        </Suspense>
      ),
    },
    {
      title: `Reach critical level (${alertGroup?.['Reach Critical Level']?.total || 0})`,
      value: TAB_ALERTS.REACH_CRITICAL_LEVEL,
      content: (
        <Suspense fallback={<LoadingCard />}>
          <ReachCriticalLevel tab={tab} />
        </Suspense>
      ),
    },
    // {
    //   title: `Abnormal consumption (${alertGroup?.['Abnormal Consumption']?.total || 0})`,
    //   value: TAB_ALERTS.ABNORMAL_CONSUMPTION,
    //   content: (
    //     <Suspense fallback={<LoadingCard />}>
    //       <AbnormalConsumption />
    //     </Suspense>
    //   ),
    // },
  ];

  const currentTab = navigation.find((nav) => nav.value === tab);

  const handleChangeNavigation = (newValue: TAB_ALERTS) => () => {
    const queryValue = tabToQueryMap[newValue] ?? String(newValue);
    navigate(`?tab=${queryValue}`);
  };

  const handleMarkAllAsRead = async () => {
    await updateMarkAllAlert(organization?.id ?? 0);
    const response = await updateMarkAllAlert(organization?.id ?? 0);

    if (response) {
      useAlertStore.getState().setState({
        unReadCount: 0,
      });

      queryClient
        .getQueryCache()
        .findAll(['alerts'])
        .forEach((queryAlert) => {
          const data: any = queryAlert.state.data;
          if (data && data.pages) {
            queryClient.setQueryData(queryAlert.queryKey, {
              ...data,
              pages: data.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((item: Alert) => ({
                  ...item,
                  is_read: true,
                })),
              })),
            });
          }
        });

      getTotalUnreadAlert();
    }
  };

  const handleFilterSubmit = useCallback(
    async (selectedItems: DropdownItem[]): Promise<boolean> => {
      try {
        const selectedIdMap = new Set(selectedItems.map(({ id }) => id));

        const changedItems: SuppressionResponse[] = alertSuppressionItems.filter(
          ({ id, active }) => {
            const isNowActive = selectedIdMap.has(id);
            return active !== isNowActive;
          },
        );

        const payload = changedItems.map(({ id }) => ({
          relation: id,
          active: selectedIdMap.has(id),
        }));

        await updateAlertSuppressionMutation(payload);
        return true;
      } catch (error) {
        return false;
      }
    },
    [alertSuppressionItems, updateAlertSuppressionMutation],
  );

  return (
    <>
      <Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-8">
          <Typography variant="heading-6">Alerts</Typography>
          {unReadCount > 0 && fetched && (
            <Typography
              variant="body-1"
              color="primary"
              className="title"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </Typography>
          )}
        </div>

        <DropdownSelect
          buttonText={DROPDOWN_BUTTON_TEXT(tab)}
          title="Alert Suppression"
          subtitle="Select items/devices to suppress alerts"
          queryKey={DROPDOWN_SELECT_QUERY_KEY[tab]}
          onSubmit={handleFilterSubmit}
          items={dropdownItems}
          buttonSubmitText="Suppress"
        />
      </Header>
      <NavigationDefault
        mainStyle={{ borderBottom: '1px solid var(--neutral-4)', margin: '0 0 30px' }}
        valueActive={tab}
        navigation={navigation}
        onChangeNavigation={handleChangeNavigation}
      />
      {currentTab?.content}
    </>
  );
};

export default AlertsPage;
